import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FIT_GOALS, FIT_SIZES, recommendSignupProducts } from "./signupFit";
import { wizardSignupProducts } from "./signupProducts";
import styles from "./signup.module.scss";

const SignupFitWizard = ({ onSelect, loadingKey }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [size, setSize] = useState("11-50");
  const [goals, setGoals] = useState([]);
  const catalog = useMemo(() => wizardSignupProducts(), []);

  const recommendedKeys = useMemo(
    () => recommendSignupProducts({ size, goals }),
    [size, goals]
  );
  const recommended = catalog.filter((product) =>
    recommendedKeys.includes(product.key)
  );
  const rest = catalog.filter((product) => !recommendedKeys.includes(product.key));

  const toggleGoal = (goal) => {
    setGoals((current) =>
      current.includes(goal)
        ? current.filter((item) => item !== goal)
        : [...current, goal]
    );
  };

  const renderCard = (product, featured) => (
    <div
      key={product.key}
      className={`${styles.productCard} ${
        product.selfServe ? styles.productCardSelfServe : ""
      } ${featured ? styles.productCardFeatured : ""}`}
    >
      <span className={styles.productBadge}>
        {product.selfServe
          ? t("signup_products.start_now")
          : t("signup_products.needs_approval")}
      </span>
      <h3>{t(product.labelKey)}</h3>
      <p>{t(product.descKey)}</p>
      <button
        className={styles.btn}
        type="button"
        disabled={Boolean(loadingKey)}
        onClick={() => onSelect(product.key)}
      >
        {loadingKey === product.key
          ? t("signup_products.starting")
          : product.selfServe
            ? t("signup_products.select")
            : t("signup_products.request_approval")}
      </button>
    </div>
  );

  return (
    <div className={styles.fitWizard}>
      <div className={styles.fitProgress} aria-hidden="true">
        <span className={step >= 1 ? styles.fitStepOn : ""}>1</span>
        <span className={step >= 2 ? styles.fitStepOn : ""}>2</span>
        <span className={step >= 3 ? styles.fitStepOn : ""}>3</span>
      </div>

      {step === 1 ? (
        <>
          <div className={styles.title}>
            <h1>{t("signup_fit.size_title")}</h1>
            <p className={styles.welcome}>{t("signup_fit.size_sub")}</p>
          </div>
          <div className={styles.fitChoices}>
            {FIT_SIZES.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`${styles.fitChoice} ${
                  size === option.id ? styles.fitChoiceOn : ""
                }`}
                onClick={() => setSize(option.id)}
              >
                {t(option.labelKey)}
              </button>
            ))}
          </div>
          <div className={styles.fitActions}>
            <button
              className={styles.btn}
              type="button"
              onClick={() => setStep(2)}
            >
              {t("signup_fit.next")}
            </button>
          </div>
        </>
      ) : null}

      {step === 2 ? (
        <>
          <div className={styles.title}>
            <h1>{t("signup_fit.goal_title")}</h1>
            <p className={styles.welcome}>{t("signup_fit.goal_sub")}</p>
          </div>
          <div className={styles.fitGoals}>
            {FIT_GOALS.map((goal) => (
              <button
                key={goal}
                type="button"
                className={`${styles.fitGoal} ${
                  goals.includes(goal) ? styles.fitChoiceOn : ""
                }`}
                onClick={() => toggleGoal(goal)}
              >
                <strong>{t(`signup_fit.goal_${goal}`)}</strong>
                <span>{t(`signup_fit.goal_${goal}_hint`)}</span>
              </button>
            ))}
          </div>
          <div className={styles.fitActions}>
            <button
              className={styles.btnSecondary}
              type="button"
              onClick={() => setStep(1)}
            >
              {t("signup_fit.back")}
            </button>
            <button
              className={styles.btn}
              type="button"
              onClick={() => setStep(3)}
            >
              {t("signup_fit.see_products")}
            </button>
          </div>
        </>
      ) : null}

      {step === 3 ? (
        <>
          <div className={styles.title}>
            <h1>{t("signup_fit.result_title")}</h1>
            <p className={styles.welcome}>{t("signup_fit.result_sub")}</p>
          </div>
          {recommended.length ? (
            <div className={styles.productGrid}>
              {recommended.map((product) => renderCard(product, true))}
            </div>
          ) : null}
          <p className={styles.fitCatalogLabel}>{t("signup_fit.all_products")}</p>
          <div className={styles.productGrid}>
            {(recommended.length ? rest : catalog).map((product) =>
              renderCard(product, false)
            )}
          </div>
          <div className={styles.fitActions}>
            <button
              className={styles.btnSecondary}
              type="button"
              onClick={() => setStep(2)}
            >
              {t("signup_fit.back")}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default SignupFitWizard;
